/**
 * Agents store — thin wrappers over react-query + the real API.
 * Components that previously imported useAgents() / renameAgent() / etc.
 * from here continue to work without changes.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { agentsApi } from "@/lib/api";

export const AGENTS_KEY = ["agents"];

export function useAgents() {
  const { data = [] } = useQuery({
    queryKey: AGENTS_KEY,
    queryFn: agentsApi.list,
  });
  return data;
}

export function useAgent(id) {
  return useQuery({
    queryKey: ["agent", id],
    queryFn: () => agentsApi.get(id),
    enabled: !!id,
  });
}

export function useRenameAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }) => agentsApi.patchName(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENTS_KEY }),
  });
}

export function useRemoveAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => agentsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENTS_KEY }),
  });
}

export function usePauseAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => agentsApi.pause(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENTS_KEY }),
  });
}

export function useResumeAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => agentsApi.resume(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENTS_KEY }),
  });
}

// Legacy compat — kept so any remaining direct mutation calls don't crash
export function renameAgent(id, name) {
  agentsApi.patchName(id, name).catch(() => {});
}
export function removeAgent(id) {
  agentsApi.delete(id).catch(() => {});
}
export function setAgentActive(id, active) {
  (active ? agentsApi.resume(id) : agentsApi.pause(id)).catch(() => {});
}
